import { Gesture, GestureDetector } from "@-/com.npmjs.react-native-gesture-handler";
import Animated, { useSharedValue } from "@-/com.npmjs.react-native-reanimated";
import type {
  AccessibilityActionEvent,
  LayoutChangeEvent,
  StyleProp,
  ViewStyle,
} from "@-/com.npmjs.react-native";
import { useEffect, useLayoutEffect, useMemo, useState } from "react";
import type { PrimaryNavigationDestination } from "@-/dev.improve.haniverse.database.schemas.actor.schema._tables.user";
import {
  usePrimaryNavigationCustomizationController,
  usePrimaryNavigationCustomizationSnapshot,
  type PrimaryNavigationCustomizationController,
} from "#primary-navigation-customization-controller";
import {
  endPrimaryNavigationCustomizationDrag,
  getPrimaryNavigationStripEditorTileSlot,
  startPrimaryNavigationCustomizationDrag,
  updatePrimaryNavigationCustomizationDrag,
} from "#primary-navigation-customization-drag";
import {
  PRIMARY_NAVIGATION_CUSTOMIZATION_LONG_PRESS_MS,
  PRIMARY_NAVIGATION_DESTINATION_CHIP_SIZE,
} from "#primary-navigation-customization-constants";
import { PrimaryNavigationDestinationChip } from "#primary-navigation-destination-chip";
import { PRIMARY_NAVIGATION_DESTINATION_PRESENTATIONS } from "#primary-navigation-destination-presentations";
import {
  getPrimaryNavigationStripSlotCenter,
  type PrimaryNavigationStripEditorEdge,
} from "#primary-navigation-strip-editor-geometry";
import {
  placeInPrimaryNavigationStrip,
  removeFromPrimaryNavigationStrip,
} from "#primary-navigation-strip-order";
import { usePrimaryNavigationCustomizationTilePlacement } from "#use-primary-navigation-customization-tile-placement";

const HALF_CHIP = PRIMARY_NAVIGATION_DESTINATION_CHIP_SIZE / 2;

type StripEditorTileProps = {
  activation: "immediate" | "long-press";
  controller: PrimaryNavigationCustomizationController;
  destination: PrimaryNavigationDestination;
  edge: PrimaryNavigationStripEditorEdge;
  order: readonly PrimaryNavigationDestination[];
};

function PrimaryNavigationStripEditorTile({
  activation,
  controller,
  destination,
  edge,
  order,
}: StripEditorTileProps) {
  const { worklets } = controller;
  const index = order.indexOf(destination);
  const isInStrip = index !== -1;
  const dragId = useSharedValue(-1);

  const style = usePrimaryNavigationCustomizationTilePlacement(() => {
    "worklet";
    const metrics = worklets.stripMetrics.get();
    const drag = worklets.drag.get();
    const placement = getPrimaryNavigationStripEditorTileSlot(
      worklets.order.get(),
      drag,
      destination,
    );
    if (metrics === null || placement === null) {
      return null;
    }
    const center = getPrimaryNavigationStripSlotCenter(
      metrics,
      placement.slot,
      placement.slotCount,
    );
    return {
      isHidden: drag?.destination === destination,
      x: center.x - HALF_CHIP,
      y: center.y - HALF_CHIP,
    };
  });

  const gesture = useMemo(() => {
    const pan = Gesture.Pan()
      .enabled(isInStrip)
      .onStart(() => {
        "worklet";
        dragId.set(startPrimaryNavigationCustomizationDrag(worklets, destination, null));
      })
      .onUpdate((event) => {
        "worklet";
        updatePrimaryNavigationCustomizationDrag(
          worklets,
          dragId.get(),
          event.translationX,
          event.translationY,
        );
      })
      .onEnd((_event, success) => {
        "worklet";
        endPrimaryNavigationCustomizationDrag(worklets, dragId.get(), success);
      })
      .onFinalize(() => {
        "worklet";
        // Covers a gesture cancelled before `onEnd`; a no-op once the drop already settled.
        endPrimaryNavigationCustomizationDrag(worklets, dragId.get(), false);
        dragId.set(-1);
      });
    return activation === "long-press"
      ? pan.activateAfterLongPress(PRIMARY_NAVIGATION_CUSTOMIZATION_LONG_PRESS_MS)
      : pan.minDistance(4);
  }, [activation, destination, dragId, isInStrip, worklets]);

  const { label } = PRIMARY_NAVIGATION_DESTINATION_PRESENTATIONS[destination];
  const isRail = edge === "left" || edge === "right";
  const accessibilityActions = [
    ...(index > 0 ? [{ label: isRail ? "Move up" : "Move left", name: "moveEarlier" }] : []),
    ...(isInStrip && index < order.length - 1
      ? [{ label: isRail ? "Move down" : "Move right", name: "moveLater" }]
      : []),
    ...(isInStrip && order.length > 1 ? [{ label: "Remove from tab bar", name: "remove" }] : []),
  ];
  const onAccessibilityAction = (event: AccessibilityActionEvent) => {
    switch (event.nativeEvent.actionName) {
      case "moveEarlier": {
        controller.edit(placeInPrimaryNavigationStrip(order, destination, index - 1));
        return;
      }
      case "moveLater": {
        controller.edit(placeInPrimaryNavigationStrip(order, destination, index + 1));
        return;
      }
      case "remove": {
        if (order.length > 1) {
          controller.edit(removeFromPrimaryNavigationStrip(order, destination));
        }
        return;
      }
    }
  };

  return (
    <GestureDetector gesture={gesture}>
      <Animated.View
        accessibilityActions={accessibilityActions}
        accessibilityHint={
          order.length > 1
            ? "Drag to rearrange, or drag it off the tab bar to remove it."
            : "Drag to rearrange."
        }
        accessibilityLabel={`${label}, ${index + 1} of ${order.length}`}
        accessible={isInStrip}
        // Tiles not in the strip stay mounted (hidden on the UI thread); keep them out of the a11y tree.
        aria-hidden={!isInStrip}
        className="absolute top-0 left-0 size-11 items-center justify-center"
        onAccessibilityAction={onAccessibilityAction}
        pointerEvents={isInStrip ? "auto" : "none"}
        style={style}
        testID={`app-primary-navigation-editor-${destination}`}
      >
        <PrimaryNavigationDestinationChip destination={destination} variant="strip" />
      </Animated.View>
    </GestureDetector>
  );
}

/**
 * The primary navigation strip while it is being customized: the same box and icon positions as
 * the live strip, but its icons are chips that drag to rearrange (or off the strip to remove), and
 * it accepts destinations dragged in from the Settings palette. Tapping never navigates.
 */
export function PrimaryNavigationStripEditor({
  activation = "immediate",
  className,
  crossCenter,
  edge,
  leadingInset,
  style,
  trailingInset,
}: {
  /** `long-press` where the strip sits inside a vertically scrolling page. */
  activation?: "immediate" | "long-press";
  className?: string;
  /** Icon center across the strip; see `PrimaryNavigationStripEditorMetrics`. */
  crossCenter: number;
  edge: PrimaryNavigationStripEditorEdge;
  leadingInset: number;
  style?: StyleProp<ViewStyle>;
  trailingInset: number;
}) {
  const controller = usePrimaryNavigationCustomizationController();
  const snapshot = usePrimaryNavigationCustomizationSnapshot(controller);
  const [size, setSize] = useState<{ height: number; width: number } | null>(null);

  useLayoutEffect(() => {
    controller?.worklets.stripMetrics.set(
      size === null
        ? null
        : {
            crossCenter,
            edge,
            height: size.height,
            leadingInset,
            trailingInset,
            width: size.width,
          },
    );
  }, [controller, crossCenter, edge, leadingInset, size, trailingInset]);
  useEffect(
    () => () => {
      // Without a mounted strip, drags have nowhere to drop and return to where they started.
      controller?.worklets.stripMetrics.set(null);
    },
    [controller],
  );

  const onLayout = (event: LayoutChangeEvent) => {
    const { height, width } = event.nativeEvent.layout;
    setSize((current) =>
      current?.height === height && current.width === width ? current : { height, width },
    );
  };
  // Rendered only while a session edits the strip.
  return controller === null || snapshot?.available == null ? null : (
    <Animated.View
      accessibilityLabel="Tab bar"
      className={className}
      collapsable={false}
      onLayout={onLayout}
      ref={controller.worklets.stripRef}
      style={style}
      testID="app-primary-navigation-editor"
    >
      {snapshot.available.map((destination) => (
        <PrimaryNavigationStripEditorTile
          activation={activation}
          controller={controller}
          destination={destination}
          edge={edge}
          key={destination}
          order={snapshot.order}
        />
      ))}
    </Animated.View>
  );
}
